import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { toast } from "react-toastify";

import KOGInputDragDropUploader, { ITypeFile } from "../inputFile/inputDragDrop";
import LoadingComponent from "../loading/loadingComponent";
import Button from "../button/button";
import SecondaryButton from "../button/secondaryButton";
import KogAutoCompleteControlled from "../inputAutoComplete/autoCompleteControlled";
import KogInputText from "../inputText/inputText";

import { parameterApi, quoteApi, quoteAttachmentApi } from "@/main/api";
import { KeyValue } from "@/main/models/common";
import { ImagesRequest, QuoteAttachmentRequest } from "@/main/models/request";
import { PartInfoByQuoteResponse } from "@/main/models/response";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { getTranslateByKey } from "@/main/services/utils";
import { convertImageToBase64 } from "@/main/services/utilsFile";

import { formatFileSize, getPartLabel, isImageFile } from "./evidenceUtils";
import * as S from "./styles";

const EVIDENCE_FILE_TYPES: ITypeFile[] = ["image", "pdf", "json", "xlsx", "docx"];
const MAXIMUM_EVIDENCE_FILES = 20;
const DEFAULT_MAX_FILE_UPLOAD_MB = 50;
const MAXIMUM_COMMENT_LENGTH = 200;
const GENERAL_ORDER_KEY = "";

interface IEvidenceFormValues {
	quoteItem: KeyValue | null;
}

interface QueuedFile {
	id: string;
	file: File;
	comment: string;
	previewUrl: string;
}

interface IEvidenceUploaderProps {
	guidQuote: string;
	guidScreen: string;
	parts: PartInfoByQuoteResponse[];
	canRelateToGeneralOrder?: boolean;
	onUploaded: () => void;
}

function revokePreviews(items: QueuedFile[]) {
	items.forEach((queuedFile) => {
		if (queuedFile.previewUrl) URL.revokeObjectURL(queuedFile.previewUrl);
	});
}

function EvidenceUploader({
	guidQuote,
	guidScreen,
	parts,
	canRelateToGeneralOrder = true,
	onUploaded,
}: IEvidenceUploaderProps) {
	const [queuedFiles, setQueuedFiles] = useState<QueuedFile[]>([]);
	const [maxFileUploadMb, setMaxFileUploadMb] = useState<number>(DEFAULT_MAX_FILE_UPLOAD_MB);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const generalOrderOption = useMemo<KeyValue>(
		() => ({
			key: GENERAL_ORDER_KEY,
			value: getTranslateByKey("evidence.generalOrder"),
		}),
		[],
	);

	const { control, reset, handleSubmit } = useForm<IEvidenceFormValues>({
		defaultValues: {
			quoteItem: canRelateToGeneralOrder ? generalOrderOption : null,
		},
	});
	const selectedPart = useWatch({ control, name: "quoteItem" });
	const selectedQuoteItemGuid = String(selectedPart?.key ?? GENERAL_ORDER_KEY);

	const partOptions = useMemo<KeyValue[]>(() => {
		const quoteItemOptions = parts.map((part) => ({
			key: part.quoteItemGuid,
			value: getPartLabel(part),
		}));

		return canRelateToGeneralOrder ? [generalOrderOption, ...quoteItemOptions] : quoteItemOptions;
	}, [parts, generalOrderOption, canRelateToGeneralOrder]);

	useEffect(() => {
		parameterApi
			.getParametersByType(ParameterTypeEnum.MaxEvidenceFileUploadMb)
			.then((response) => {
				const parameterValue = Number(response.data.result?.[0]?.value);
				if (!Number.isNaN(parameterValue) && parameterValue > 0) {
					setMaxFileUploadMb(parameterValue);
				}
			})
			.catch(() => setMaxFileUploadMb(DEFAULT_MAX_FILE_UPLOAD_MB));
	}, []);

	function handleAddFiles(newFiles: File[]) {
		if (newFiles.length === 0) return;

		setQueuedFiles((currentFiles) => [
			...currentFiles,
			...newFiles.map((file) => ({
				id: `${file.name}-${file.size}-${file.lastModified}`,
				file,
				comment: "",
				previewUrl: isImageFile(file) ? URL.createObjectURL(file) : "",
			})),
		]);
	}

	function handleRemoveFile(id: string) {
		setQueuedFiles((currentFiles) => {
			revokePreviews(currentFiles.filter((queuedFile) => queuedFile.id === id));
			return currentFiles.filter((queuedFile) => queuedFile.id !== id);
		});
	}

	function handleChangeComment(id: string, comment: string) {
		setQueuedFiles((currentFiles) =>
			currentFiles.map((queuedFile) => (queuedFile.id === id ? { ...queuedFile, comment } : queuedFile)),
		);
	}

	function handleCancel() {
		revokePreviews(queuedFiles);
		setQueuedFiles([]);
		reset();
	}

	async function saveImages(items: QueuedFile[]) {
		const imagesRequest: ImagesRequest[] = await Promise.all(
			items.map(async (queuedFile) => {
				const convertedImage = await convertImageToBase64(queuedFile.file);
				return {
					imageUpload: `data:${convertedImage.type};base64,${convertedImage.base64Image}`,
					imageName: convertedImage.name,
					quoteItemGuid: selectedQuoteItemGuid || undefined,
					comment: queuedFile.comment || undefined,
				};
			}),
		);

		const response = await quoteApi.saveQuoteImages({
			guidQuote,
			guidScreen,
			imagesRequest,
		});

		return response.data.success !== false;
	}

	async function saveAttachments(items: QueuedFile[]) {
		const attachmentsRequest: QuoteAttachmentRequest[] = items.map((queuedFile) => ({
			file: queuedFile.file,
			comment: queuedFile.comment,
			quoteItemGuid: selectedQuoteItemGuid || undefined,
		}));

		const response = await quoteAttachmentApi.saveAttachments({
			quoteGuid: guidQuote,
			attachmentsRequest,
		});

		return response.data.success !== false;
	}

	async function handleAttach() {
		const imageFiles = queuedFiles.filter((queuedFile) => isImageFile(queuedFile.file));
		const documentFiles = queuedFiles.filter((queuedFile) => !isImageFile(queuedFile.file));
		const failedFiles: QueuedFile[] = [];

		setIsSaving(true);

		if (imageFiles.length > 0) {
			const savedImages = await saveImages(imageFiles).catch(() => false);
			if (!savedImages) failedFiles.push(...imageFiles);
		}

		if (documentFiles.length > 0) {
			const savedAttachments = await saveAttachments(documentFiles).catch(() => false);
			if (!savedAttachments) failedFiles.push(...documentFiles);
		}

		const savedFiles = queuedFiles.filter((queuedFile) => !failedFiles.some((failed) => failed.id === queuedFile.id));

		revokePreviews(savedFiles);

		setIsSaving(false);
		setQueuedFiles(failedFiles);

		if (savedFiles.length === 0) return;

		toast.success(getTranslateByKey("information.imagesSavedSuccessfully"));
		onUploaded();

		if (failedFiles.length === 0) reset();
	}

	if (isSaving) return <LoadingComponent />;

	return (
		<S.EvidenceUploaderContainer id="id-evidence-uploader">
			<KOGInputDragDropUploader
				files={queuedFiles.map((queuedFile) => queuedFile.file)}
				typeFiles={EVIDENCE_FILE_TYPES}
				maxFiles={MAXIMUM_EVIDENCE_FILES}
				maxTotalSize={maxFileUploadMb}
				isFullScreenDragDrop
				onFilesChange={handleAddFiles}
			/>
			{queuedFiles.length > 0 && (
				<>
					<S.EvidenceSectionTitle>{getTranslateByKey("evidence.filesToAttach")}</S.EvidenceSectionTitle>

					<KogAutoCompleteControlled
						id="id-evidence-select-part"
						control={control}
						nameCustom="quoteItem"
						label={getTranslateByKey("evidence.relateToPart")}
						options={partOptions}
						rulesCustom={{ required: !canRelateToGeneralOrder }}
						onChangeCustom={() => {}}
					/>

					{queuedFiles.map((queuedFile) => (
						<S.EvidenceQueueItem key={queuedFile.id}>
							<S.EvidenceQueueItemHeader>
								{isImageFile(queuedFile.file) ? (
									<S.EvidenceThumbnail src={queuedFile.previewUrl} alt={queuedFile.file.name} />
								) : (
									<InsertDriveFileOutlinedIcon fontSize="small" />
								)}
								<S.EvidenceQueueItemInfo>
									<S.LabelFileName>{queuedFile.file.name}</S.LabelFileName>
									<S.EvidenceFileSize>{formatFileSize(queuedFile.file.size)}</S.EvidenceFileSize>
								</S.EvidenceQueueItemInfo>
								<S.ButtonDeleteFile
									id={`id-evidence-btn-remove-${queuedFile.id}`}
									onClick={() => handleRemoveFile(queuedFile.id)}
								>
									<CloseIcon fontSize="small" />
								</S.ButtonDeleteFile>
							</S.EvidenceQueueItemHeader>

							<KogInputText
								value={queuedFile.comment}
								placeholder={getTranslateByKey("evidence.addComment")}
								inputProps={{ maxLength: MAXIMUM_COMMENT_LENGTH }}
								onChangeCustom={(comment) => handleChangeComment(queuedFile.id, comment)}
							/>
						</S.EvidenceQueueItem>
					))}

					<S.EvidenceFooter>
						<SecondaryButton id="id-evidence-btn-cancel" type="button" onClick={handleCancel}>
							{getTranslateByKey("information.cancel")}
						</SecondaryButton>
						<Button id="id-evidence-btn-attach" type="button" onClick={handleSubmit(handleAttach)}>
							{`${getTranslateByKey("evidence.attach")} (${queuedFiles.length})`}
						</Button>
					</S.EvidenceFooter>
				</>
			)}
		</S.EvidenceUploaderContainer>
	);
}

export default EvidenceUploader;
